import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn, formatPercent } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowRightLeft,
  School,
  Database,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Clock,
  XCircle,
  List,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { migrationV1V2Api } from './api/migration-v1-v2';
import type { AutoCatchUpStats, MigrationProject, PaginatedResponse } from './types';
import type { MigrationSchoolEntry } from './api/migration-v1-v2';
import { ProjectStatusBadge } from './components/ProjectStatusBadge';
import type { ProjectStatus } from './types';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  onClick?: () => void;
}

function StatCard({ title, value, icon: Icon, color, onClick }: StatCardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm transition-all',
        onClick && 'cursor-pointer hover:shadow-md hover:-translate-y-0.5'
      )}
    >
      <div className="p-4 flex items-center gap-3">
        <div className={cn('p-2.5 rounded-lg', color)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </div>
    </div>
  );
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  // Ouverture du dossier : admins (manage) partout ; sinon uniquement le
  // responsable assigné (les autres voient la liste sans lien).
  const canOpenProject = (project: MigrationProject) =>
    hasPermission('action.migration_v1_v2.manage') || project.assignedTo?.id === user?.id;

  const [stats, setStats] = useState({ totalSchools: 0, notStarted: 0, inProgress: 0, completed: 0, failed: 0 });
  const [recentProjects, setRecentProjects] = useState<MigrationProject[]>([]);
  const [autoCatchUp, setAutoCatchUp] = useState<AutoCatchUpStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch stats from schools endpoint
        const schoolsResponse = await migrationV1V2Api.getSchools({ limit: 1 });
        if (schoolsResponse.meta.stats) {
          setStats(schoolsResponse.meta.stats);
        }
        // Fetch recent active projects
        const projectsResponse = await migrationV1V2Api.getProjects({ limit: 10 });
        setRecentProjects(projectsResponse.data);
        // Rattrapage automatique : ce que la tâche programmée a repris depuis
        // la bascule (la liste sert seulement à récupérer les compteurs).
        const catchUpResponse = await migrationV1V2Api.getAutoCatchUpSweeps({ perPage: 1 });
        setAutoCatchUp(catchUpResponse.stats);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalMigrated = stats.completed + stats.inProgress + stats.failed;
  const globalProgress = stats.totalSchools > 0 ? (stats.completed / stats.totalSchools) * 100 : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
            <ArrowRightLeft className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Migration V1 → V2
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Tableau de bord
            </p>
          </div>
        </div>
        <Button asChild>
          <Link to="/migration-v1-v2/schools">
            <List className="h-4 w-4" />
            Voir toutes les écoles
          </Link>
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          title="Écoles éligibles"
          value={stats.totalSchools}
          icon={School}
          color="bg-blue-100 text-blue-600"
          onClick={() => navigate('/migration-v1-v2/schools')}
        />
        <StatCard
          title="Pas commencées"
          value={stats.notStarted}
          icon={Database}
          color="bg-gray-100 text-gray-600"
          onClick={() => navigate('/migration-v1-v2/schools?migrationStatus=not_started')}
        />
        <StatCard
          title="En cours"
          value={stats.inProgress}
          icon={Loader2}
          color="bg-orange-100 text-orange-600"
          onClick={() => navigate('/migration-v1-v2/schools?migrationStatus=in_progress')}
        />
        <StatCard
          title="Terminées"
          value={stats.completed}
          icon={CheckCircle}
          color="bg-green-100 text-green-600"
          onClick={() => navigate('/migration-v1-v2/schools?migrationStatus=completed')}
        />
        <StatCard
          title="Échouées"
          value={stats.failed}
          icon={AlertTriangle}
          color="bg-red-100 text-red-600"
          onClick={() => navigate('/migration-v1-v2/schools?migrationStatus=failed')}
        />
      </div>

      {/* Global progress */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30 py-4">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-violet-500" />
              Progression globale
            </span>
            <span className="text-sm font-normal text-gray-500">
              {stats.completed}/{stats.totalSchools} écoles migrées
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-2">
            <Progress value={globalProgress} className="h-4" />
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{formatPercent(globalProgress)}%</span>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  {stats.completed} terminées
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-orange-500" />
                  {stats.inProgress} en cours
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  {stats.failed} échouées
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rattrapage automatique des utilisateurs : ce que la tâche programmée
          a repris toute seule depuis les bascules. */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30 py-4">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-sky-500" />
              Rattrapage automatique
            </span>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/migration-v1-v2/rattrapage">
                Voir le journal
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-bold text-sky-600 dark:text-sky-300 tabular-nums">
                {autoCatchUp?.totalAdded ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Éléments migrés</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                {autoCatchUp?.added30d ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sur 30 jours</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                {autoCatchUp?.sweeps30d ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Passes sur 30 jours</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {autoCatchUp?.lastSweepAt ? formatDate(autoCatchUp.lastSweepAt) : '—'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {autoCatchUp?.lastSweepAt ? 'Dernière passe' : 'Jamais exécutée'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent projects */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30 py-4">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              Projets récents
            </span>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/migration-v1-v2/schools?migrationStatus=in_progress">
                Voir tout
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {recentProjects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucun projet de migration en cours.
              <br />
              <Button variant="outline" className="mt-3" asChild>
                <Link to="/migration-v1-v2/schools">
                  Commencer une migration
                </Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {recentProjects.map((project) => {
                const percent = project.totalRecords > 0
                  ? (project.processedRecords / project.totalRecords) * 100
                  : 0;
                const openable = canOpenProject(project);
                return (
                  <div
                    key={project.id}
                    onClick={openable ? () => navigate(`/migration-v1-v2/${project.id}`) : undefined}
                    className={cn(
                      'flex items-center gap-4 py-3 -mx-6 px-6 transition-colors',
                      openable && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {project.school.name}
                        </span>
                        <Badge variant="outline" className="font-mono text-xs shrink-0">
                          {project.school.code}
                        </Badge>
                        <ProjectStatusBadge status={project.status} />
                      </div>
                      {project.totalRecords > 0 && (
                        <div className="flex items-center gap-3 mt-1">
                          <Progress value={percent} className="flex-1 h-1.5" />
                          <span className="text-xs text-gray-500 shrink-0">{formatPercent(percent)}%</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-gray-400">
                        {formatDate(project.startedAt || project.createdAt)}
                      </div>
                      {project.failedRecords > 0 && (
                        <div className="flex items-center gap-1 text-xs text-red-500 justify-end">
                          <XCircle className="h-3 w-3" />
                          {project.failedRecords} erreurs
                        </div>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
